// Intercom messenger utilities
// Use the named export: the package is CommonJS and its default export does not
// reliably resolve to a callable under Vite's dependency interop, which caused
// "Intercom is not a function" crashes.
import { Intercom } from '@intercom/messenger-js-sdk';

const INTERCOM_APP_ID = 'hx49czo0';

/**
 * Fetches a signed JWT from the server for Intercom identity verification.
 */
async function fetchIntercomJWT(): Promise<string | null> {
  try {
    const res = await fetch('/api/intercom/token');
    if (!res.ok) return null;
    const data = await res.json();
    return data.token ?? null;
  } catch {
    console.warn('Failed to fetch Intercom JWT');
    return null;
  }
}

/**
 * Initializes Intercom with the authenticated user's identity and JWT verification.
 */
export async function initIntercom(user: {
  id: string | number;
  firstName?: string;
  lastName?: string;
  email: string;
  createdAt?: string | Date;
  primaryColor?: string;
  themeMode?: 'light' | 'dark' | 'system';
}) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
  const createdAtUnix = user.createdAt
    ? Math.floor(new Date(user.createdAt).getTime() / 1000)
    : undefined;

  const jwt = await fetchIntercomJWT();

  if (typeof Intercom !== 'function') {
    console.warn('Intercom SDK is unavailable; skipping initialization.');
    return;
  }

  try {
    Intercom({
      app_id: INTERCOM_APP_ID,
      user_id: String(user.id),
      name,
      email: user.email,
      created_at: createdAtUnix,
      ...(jwt ? { intercom_user_jwt: jwt } : {}),
      ...(user.primaryColor ? { action_color: user.primaryColor, background_color: user.primaryColor } : {}),
      ...(user.themeMode ? { theme_mode: user.themeMode } : {}),
    });
  } catch (err) {
    console.warn('Failed to initialize Intercom messenger:', err);
  }
}

/**
 * Updates Intercom messenger settings without a full re-boot.
 * Useful for reacting to theme changes.
 */
export function updateIntercom(settings: Record<string, unknown>) {
  if (window.Intercom) {
    window.Intercom('update', settings);
  }
}


/**
 * Shuts down Intercom and clears user data.
 * Call this on logout to ensure clean state for the next user.
 */
export function shutdownIntercom() {
  if (window.Intercom) {
    window.Intercom('shutdown');
  }
}
